import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { request } from './helper';
import { Agency, AgenciesState } from '@/types'

// **Initial State**
const initialState: AgenciesState = {
  agencies: [],
  fetchStatus: 'idle',
  status: 'idle',
};

// **Thunks**
export const agenciesGet = createAsyncThunk<Agency[], void, { rejectValue: string }>(
  'agencies/get',
  async (_, { rejectWithValue }) => {
    const response = await request('admin/agencies');
    if (response.success) return response.data;
    return rejectWithValue(response.error || 'Failed to fetch agencies');
  }
);

export const agenciesGetForUser = createAsyncThunk<Agency[], void, { rejectValue: string }>(
  'agencies/getForUser',
  async (_, { rejectWithValue }) => {
    const response = await request('api/agencies');
    if (response.success) return response.data;
    return rejectWithValue(response.error || 'Failed to fetch user agencies');
  }
);

export const agenciesCreate = createAsyncThunk<any, Record<string, any>, { rejectValue: string }>(
  'agencies/create',
  async (data, { rejectWithValue }) => {
    const response = await request('admin/agencies', 'POST', data);
    if (response.success) return response.data;
    return rejectWithValue(response.error || 'Failed to create agency');
  }
);

export const agenciesDelete = createAsyncThunk<number, number, { rejectValue: string }>(
  'agencies/delete',
  async (id, { rejectWithValue }) => {
    const response = await request(`admin/agencies/${id}`, 'DELETE');
    if (response.success) return id; // Return the deleted agency's ID
    return rejectWithValue(response.error || 'Failed to delete agency');
  }
);

export const agenciesLoginAsUser = createAsyncThunk<void, number, { rejectValue: string }>(
  'agencies/loginAsUser',
  async (id, { rejectWithValue }) => {
    const response = await request(`admin/agencies/login-as-user/${id}`);
    if (response.success) return;
    return rejectWithValue(response.error || 'Failed to login as agency user');
  }
);

// **Slice**
export const agenciesSlice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const setFetchLoading = (state: AgenciesState) => {
      state.fetchStatus = 'loading';
    };
    const setFetchFailed = (state: AgenciesState) => {
      state.fetchStatus = 'failed';
    };

    const setActionLoading = (state: AgenciesState) => {
      state.status = 'loading';
    };
    const setActionFailed = (state: AgenciesState) => {
      state.status = 'failed';
    };

    // Get Agencies
    builder
      .addCase(agenciesGet.pending, setFetchLoading)
      .addCase(agenciesGet.fulfilled, (state, { payload }: PayloadAction<Agency[]>) => {
        state.fetchStatus = 'success';
        state.agencies = payload;
      })
      .addCase(agenciesGet.rejected, setFetchFailed);

    // Get Agencies for User
    builder
      .addCase(agenciesGetForUser.pending, setFetchLoading)
      .addCase(agenciesGetForUser.fulfilled, (state, { payload }: PayloadAction<Agency[]>) => {
        state.fetchStatus = 'success';
        state.agencies = payload;
      })
      .addCase(agenciesGetForUser.rejected, setFetchFailed);

    // Create Agency
    builder
      .addCase(agenciesCreate.pending, setActionLoading)
      .addCase(agenciesCreate.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(agenciesCreate.rejected, setActionFailed);

    // Delete Agency
    builder
      .addCase(agenciesDelete.pending, setActionLoading)
      .addCase(agenciesDelete.fulfilled, (state, { payload }: PayloadAction<number>) => {
        state.status = 'success';
        state.agencies = state.agencies.filter((agency) => agency.id !== payload);
      })
      .addCase(agenciesDelete.rejected, setActionFailed);

    // Login as User
    builder
      .addCase(agenciesLoginAsUser.pending, setActionLoading)
      .addCase(agenciesLoginAsUser.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(agenciesLoginAsUser.rejected, setActionFailed);
  },
});

// **Export Reducer**
export default agenciesSlice.reducer;